import type {
  AnalyticsPayload,
  AnalyticsPlatform,
  LoginAnalytics,
} from "src/interfaces/LoginAnalytics";
import { BUILD_VERSION } from "src/versioning/version";

export type { AnalyticsPayload, AnalyticsPlatform, LoginAnalytics };

export const useGetAnalyticsData = () => {
  const getAnalyticsData = (): AnalyticsPayload => {
    const isElectron = window.navigator.userAgent.indexOf("Electron") !== -1;
    const appType = isElectron ? "desktop" : "web";
    const platform = resolvePlatform(isElectron);
    const releaseVersion = BUILD_VERSION?.releaseVersion ?? "unknown";
    const semver = stripVersionPrefix(releaseVersion);
    const buildDate = formatBuildDate(BUILD_VERSION?.buildDate);
    const appVersion = `${platform}/${semver}/${buildDate}`;

    const userAgent = navigator.userAgent;
    const osInfo = getOSInfo(userAgent);

    const analytics: LoginAnalytics = {
      appType,
      platform,
      buildNumber: BUILD_VERSION?.buildNumber ?? 0,
      releaseVersion,
      os: osInfo.os,
      osVersion: osInfo.version,
    };
    if (appType === "web") {
      analytics.browser = getBrowserInfo(userAgent);
    }
    if (isElectron) {
      const electronVersion = getElectronVersion(userAgent);
      if (electronVersion) analytics.electronVersion = electronVersion;
    }

    return { appVersion, analytics };
  };

  return getAnalyticsData;
};

/**
 * App channel for appVersion — not the browser OS.
 * Desktop prefers BUILD_VERSION.platform from the packaging step.
 */
const resolvePlatform = (isElectron: boolean): AnalyticsPlatform => {
  if (!isElectron) return "web";

  const fromBuild = BUILD_VERSION?.platform;
  if (fromBuild === "win" || fromBuild === "mac" || fromBuild === "linux") {
    return fromBuild;
  }

  return detectDesktopPlatformFromUa(navigator.userAgent);
};

const detectDesktopPlatformFromUa = (userAgent: string): AnalyticsPlatform => {
  const ua = userAgent.toLowerCase();
  if (ua.includes("windows")) return "win";
  if (ua.includes("mac")) return "mac";
  return "linux";
};

const stripVersionPrefix = (version: string): string =>
  version.startsWith("v") ? version.slice(1) : version;

/**
 * Prefer DD_MM_YYYY; convert legacy YYYY_MM_DD if still present in version.js.
 */
const formatBuildDate = (buildDate?: string): string => {
  if (!buildDate) return "00_00_0000";

  const dmy = buildDate.match(/^(\d{2})_(\d{2})_(\d{4})$/);
  if (dmy) return buildDate;

  const ymd = buildDate.match(/^(\d{4})_(\d{2})_(\d{2})$/);
  if (ymd) return `${ymd[3]}_${ymd[2]}_${ymd[1]}`;

  return buildDate;
};

/**
 * Extract OS information from user agent
 */
const getOSInfo = (userAgent: string): { os: string; version: string } => {
  const ua = userAgent.toLowerCase();

  if (ua.indexOf("windows") !== -1) {
    const match = ua.match(/windows nt ([\d.]+)/);
    const ntVersion = match ? match[1] : "unknown";
    return { os: "Windows", version: mapWindowsNTVersion(ntVersion) };
  }

  if (ua.indexOf("mac os x") !== -1) {
    const match = ua.match(/mac os x ([\d_]+)/);
    const version = match ? match[1].replace(/_/g, ".") : "unknown";
    return { os: "macOS", version };
  }

  if (ua.indexOf("linux") !== -1) {
    if (ua.indexOf("android") !== -1) {
      const match = ua.match(/android ([\d.]+)/);
      return { os: "Android", version: match ? match[1] : "unknown" };
    }
    return { os: "Linux", version: "unknown" };
  }

  if (ua.indexOf("iphone") !== -1 || ua.indexOf("ipad") !== -1) {
    const match = ua.match(/os ([\d_]+)/);
    const version = match ? match[1].replace(/_/g, ".") : "unknown";
    return { os: "iOS", version };
  }

  return { os: "unknown", version: "unknown" };
};

/**
 * Map Windows NT version to friendly name
 */
const mapWindowsNTVersion = (ntVersion: string): string => {
  const versionMap: Record<string, string> = {
    "10.0": "10/11",
    "6.3": "8.1",
    "6.2": "8",
    "6.1": "7",
    "6.0": "Vista",
  };
  return versionMap[ntVersion] || ntVersion;
};

/**
 * Extract browser information
 */
const getBrowserInfo = (userAgent: string): string => {
  const ua = userAgent.toLowerCase();

  if (ua.indexOf("edg") !== -1) return "Edge";
  if (ua.indexOf("chrome") !== -1) return "Chrome";
  if (ua.indexOf("safari") !== -1) return "Safari";
  if (ua.indexOf("firefox") !== -1) return "Firefox";
  if (ua.indexOf("opera") !== -1 || ua.indexOf("opr") !== -1) return "Opera";

  return "unknown";
};

/**
 * Extract Electron version from user agent
 */
const getElectronVersion = (userAgent: string): string | undefined => {
  const match = userAgent.match(/Electron\/([\d.]+)/);
  return match ? match[1] : undefined;
};
